import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Leaf, Star } from "lucide-react";
import { useLandingData } from "@/context/LandingDataContext";

const ICONS = { Sparkles, ShieldCheck, Leaf, Star };

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Benefits = () => {
  const { content } = useLandingData();
  const benefitsSection = content?.benefitsSection || {
    overline: "— Mengapa DA'VM",
    title: "Filosofi kecantikan",
    italicTitle: "yang mewah & jujur.",
    description: "Kami percaya kecantikan sejati lahir dari kepercayaan diri. Setiap formula DA'VM dirancang untuk merawat kulit Anda — dengan integritas, kualitas, dan sentuhan mewah."
  };
  const benefits = content?.benefits || [];

  return (
    <section
      id="manfaat"
      data-testid="benefits-section"
      className="relative bg-[#F5F2EB] py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20 items-end">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            className="md:col-span-7"
          >
            <p className="text-xs tracking-[0.4em] uppercase text-[#8A6E52] mb-6">
              {benefitsSection.overline}
            </p>
            <h2
              data-testid="benefits-title"
              className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A] font-light"
            >
              {benefitsSection.title}
              <br />
              <em className="italic font-normal text-[#8A6E52]">
                {benefitsSection.italicTitle}
              </em>
            </h2>
          </motion.div>
          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            custom={1}
            className="md:col-span-5 text-base md:text-lg text-[#5C5C5C] leading-relaxed"
          >
            {benefitsSection.description}
          </motion.p>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {benefits.map((b, i) => {
            const Icon = ICONS[b.icon] || Sparkles;
            return (
              <motion.div
                key={b.title}
                data-testid={`benefit-card-${i}`}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                custom={i}
                variants={fadeUp}
                className="group relative bg-[#FDFBF7] border border-[#E8E3D9] p-8 md:p-10 hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-full bg-[#F5F2EB] border border-[#E8E3D9] flex items-center justify-center mb-8 group-hover:bg-[#BCA38F] group-hover:border-[#BCA38F] transition-colors duration-500">
                  <Icon className="h-5 w-5 text-[#8A6E52] group-hover:text-[#FDFBF7] transition-colors duration-500" />
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] font-normal mb-4">
                  {b.title}
                </h3>
                <p className="text-sm md:text-base text-[#5C5C5C] leading-relaxed">
                  {b.description}
                </p>
                <div className="absolute top-6 right-6 text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/30">
                  0{i + 1}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
